import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserData, isAuthenticated } from '../services/api';
import { UserData } from '../types/Api';

interface UserContextType {
  userData: UserData | null;
  isLoading: boolean;
  error: string | null;
  refetchUserData: () => Promise<void>;
  setUserDataManually: (data: UserData) => void;
  clearUserData: () => void;
  adaptations: Record<string, string> | null;
  adapt: (text: string) => string;
}

const defaultContext: UserContextType = {
  userData: null,
  isLoading: true,
  error: null,
  refetchUserData: async () => {},
  setUserDataManually: () => {},
  clearUserData: () => {},
  adaptations: null,
  adapt: (text: string) => text
};

const UserContext = createContext<UserContextType>(defaultContext);

export const useUser = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adaptations, setAdaptations] = useState<Record<string, string> | null>(null);

  const fetchUserData = async () => {
    // Kullanıcı giriş yapmamışsa, API çağrısı yapmayalım
    if (!isAuthenticated()) {
      setUserData(null);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUserData();
      setUserData(data);
      
      // Adaptations'ı userData'dan al
      if (data?.Adaptations) {
        setAdaptations(data.Adaptations);
      }
    } catch (err) {
      console.error('Kullanıcı bilgileri alınamadı:', err);
      setError('Kullanıcı bilgileri alınamadı');
      setUserData(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Harici veri ile userData'yı güncellemek için fonksiyon
  const setUserDataManually = (data: UserData) => {
    setUserData(data);
    setIsLoading(false);
    setError(null);
  };

  // Kullanıcı verilerini temizle (logout için)
  const clearUserData = () => {
    setUserData(null);
    setAdaptations(null);
    setIsLoading(false);
    setError(null);
  };

  // Adaptation fonksiyonu - basit metin değiştirme
  const adapt = (text: string): string => {
    return adaptations?.[text] || text;
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        isLoading,
        error,
        refetchUserData: fetchUserData,
        setUserDataManually,
        clearUserData,
        adaptations,
        adapt
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext; 